import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import client from '../api/client';

type Role = 'QC' | 'CREATIVE' | 'ADMIN' | 'NONE';
type EffectiveRole = 'QC' | 'CREATIVE';

interface UserInfo {
  email: string;
  name: string;
  role: Role;
}

interface RoleContextType {
  actualRole: Role;
  effectiveRole: EffectiveRole;
  isAdmin: boolean;
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: Error | null;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Module-level state for accessing role outside of React context (e.g., in router guards)
let currentActualRole: Role = 'NONE';
let currentEffectiveRole: EffectiveRole = 'CREATIVE';
let isAuthenticated: boolean = false;

/**
 * Get the current user role and auth state synchronously (for use in router guards)
 */
export function getCurrentRole(): {
  actualRole: Role;
  effectiveRole: EffectiveRole;
  isAuthenticated: boolean;
} {
  return {
    actualRole: currentActualRole,
    effectiveRole: currentEffectiveRole,
    isAuthenticated,
  };
}

interface RoleProviderProps {
  children: ReactNode;
}

export function RoleProvider({ children }: RoleProviderProps) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [effectiveRole, setEffectiveRoleState] = useState<EffectiveRole>('QC');

  // Get Okta auth state
  const oktaAuthContext = useOktaAuth();
  const authState = oktaAuthContext?.authState;

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Skip user fetch on Okta callback route - let LoginCallback complete token exchange first
        if (window.location.pathname === '/login/callback') {
          console.log('⏭️  On callback route - skipping user fetch');
          setIsLoading(false);
          return;
        }

        setIsLoading(true);

        // Wait for authState to be initialized
        if (!authState) {
          console.log('⏳ Waiting for auth state...');
          return;
        }

        // Handle unauthenticated state
        if (!authState.isAuthenticated) {
          console.log('❌ Not authenticated');
          setUserInfo({
            email: '',
            name: 'Unknown User',
            role: 'NONE',
          });
          currentActualRole = 'NONE';
          currentEffectiveRole = 'CREATIVE';
          isAuthenticated = false;
          setIsLoading(false);
          return;
        }

        // Fetch user info from backend
        console.log('✅ Authenticated - fetching user info from /user/me...');
        const response = await client.get<UserInfo>('/user/me');
        const data = response.data;
        console.log('✅ User info received:', data);

        setUserInfo(data);
        currentActualRole = data.role;
        isAuthenticated = true;

        // Determine effective role (admins always get QC view, others use their actual role)
        const newEffectiveRole = data.role === 'ADMIN' || data.role === 'QC'
          ? 'QC'
          : 'CREATIVE';

        setEffectiveRoleState(newEffectiveRole);
        currentEffectiveRole = newEffectiveRole;

        console.log(`👤 ${data.role.toUpperCase()} user - effective role: ${newEffectiveRole}`);

        setError(null);
        setIsLoading(false);
      } catch (err) {
        console.error('❌ Failed to fetch user info:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch user info'));

        // Set safe defaults on error
        setUserInfo({ email: '', name: 'Unknown User', role: 'NONE' });
        setEffectiveRoleState('CREATIVE');
        currentActualRole = 'NONE';
        currentEffectiveRole = 'CREATIVE';
        isAuthenticated = false;
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [authState]);

  const contextValue: RoleContextType = {
    actualRole: userInfo?.role || 'NONE',
    effectiveRole,
    isAdmin: userInfo?.role === 'ADMIN',
    userInfo,
    isLoading,
    error,
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole(): RoleContextType {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
